// =============================================
// usePublishQueue — hook for publish queue CRUD + realtime
// =============================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  addToPublishQueue,
  processPublishQueue,
  cancelScheduled,
  clearPublishQueueHistory,
  getPublishStatus,
  type PublishStatus,
} from "@/services/publishQueueService";
import type { PublishQueueItem } from "@/types/thematicMaps";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

export function usePublishQueue(mapId: string) {
  const [items, setItems] = useState<PublishQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<PublishStatus>({
    queued: 0, scheduled: 0, publishing: 0, published: 0, failed: 0,
  });
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("publish_queue")
      .select("*")
      .eq("map_id", mapId)
      .order("scheduled_at", { ascending: true });

    setItems((data || []) as PublishQueueItem[]);
    const s = await getPublishStatus(mapId);
    setStatus(s);
    setLoading(false);
  }, [mapId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const { isLive } = useRealtimeSubscription({
    channel: `pubq_${mapId || 'none'}`,
    table: "publish_queue",
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(mapId),
    onChange: fetchItems,
  });

  const addToQueue = useCallback(
    async (nodeIds: string[], websiteId: string) => {
      setLoading(true);
      try {
        const added = await addToPublishQueue(nodeIds, mapId, websiteId);
        toast({
          title: "Dodano do kolejki publikacji",
          description: `${added} artykułów zaplanowanych.`,
        });
        await fetchItems();
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nieznany błąd",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [mapId, toast, fetchItems],
  );

  const publish = useCallback(async (forceAll = false) => {
    setLoading(true);
    try {
      const result = await processPublishQueue(forceAll);
      toast({
        title: "Publikacja zakończona",
        description: `Opublikowano: ${result.published}, błędy: ${result.failed}`,
      });
      await fetchItems();
    } catch (err) {
      toast({
        title: "Błąd publikacji",
        description: err instanceof Error ? err.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, fetchItems]);

  const cancel = useCallback(
    async (queueId: string) => {
      await cancelScheduled(queueId);
      await fetchItems();
    },
    [fetchItems],
  );

  const clearHistory = useCallback(async () => {
    setLoading(true);
    try {
      const deleted = await clearPublishQueueHistory(mapId);
      toast({
        title: "Historia wyczyszczona",
        description: `${deleted} rekordów usuniętych (cancelled + failed).`,
      });
      await fetchItems();
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [mapId, toast, fetchItems]);

  return {
    items,
    loading,
    isLive,
    status,
    addToQueue,
    publish,
    cancel,
    clearHistory,
    refresh: fetchItems,
  };
}
